/*
 * Motail mixbox — "Stel je eigen pakket samen"
 * --------------------------------------------
 * Drives sections/motail-mixbox.liquid. The shopper picks a pack size (a variant of
 * the mixbox product) and fills exactly that many slots with flavours. The pack is
 * added as ONE line item with one line item property per flavour
 * ("Pornstar Martini" => "4").
 *
 * State is a `sequence` of flavour ids in the order they were added, so a new
 * cocktail always lands in the next free slot (only that slot animates) and "−"
 * removes the most recently added one of that flavour.
 *
 * Reuses the Flow theme surface, like assets/motail-quick-add.js:
 *   window.routes.cart_add_url / cart_url
 *   window.eventBus.emit('update:cart:drawer' | 'open:cart:drawer')
 *   window.wetheme.updateCartCount(cart)
 *
 * Note: avoid instance property names that shadow HTMLElement accessors
 * (popover, title, hidden, …) — see the quick-add history.
 */

const STORAGE_PREFIX = 'motail-mixbox:';

class MotailMixbox extends HTMLElement {
  connectedCallback() {
    if (this.isReady) return;
    this.isReady = true;

    if (window.wetheme && window.wetheme.webcomponentRegistry) {
      window.wetheme.webcomponentRegistry.register({ key: 'motail-mixbox' });
    }

    const configElement = this.querySelector('[data-mixbox-config]');
    if (!configElement) return;
    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Motail mixbox: invalid config', error);
      return;
    }

    this.sizes = (this.config.sizes || []).slice().sort((a, b) => a.size - b.size);
    if (!this.sizes.length) return;
    this.flavours = (this.config.flavours || []).map((flavour) => ({ ...flavour, id: String(flavour.id) }));
    this.flavourById = new Map(this.flavours.map((flavour) => [flavour.id, flavour]));
    this.sequence = [];
    this.isBusy = false;
    this.storageKey = `${STORAGE_PREFIX}${this.config.productId}`;
    this.sectionElement = this.closest('.motail-mixbox');

    this.els = {
      sizeInputs: Array.from(this.querySelectorAll('[data-mixbox-size-input]')),
      cards: new Map(
        Array.from(this.querySelectorAll('[data-mixbox-flavour]')).map((card) => [card.getAttribute('data-mixbox-flavour'), card])
      ),
      flavourStep: this.querySelector('.mixbox__step--flavours'),
      sizeText: this.querySelector('[data-mixbox-size-text]'),
      fill: this.querySelector('[data-mixbox-fill]'),
      fillLabel: this.querySelector('[data-mixbox-fill-label]'),
      clear: this.querySelector('[data-mixbox-clear]'),
      box: this.querySelector('[data-mixbox-box]'),
      boxTitle: this.querySelector('[data-mixbox-box-title]'),
      total: this.querySelector('[data-mixbox-total]'),
      size: this.querySelector('[data-mixbox-size]'),
      progress: this.querySelector('[data-mixbox-progress]'),
      slots: this.querySelector('[data-mixbox-slots]'),
      overflow: this.querySelector('[data-mixbox-overflow]'),
      status: this.querySelector('[data-mixbox-status]'),
      upsell: this.querySelector('[data-mixbox-upsell]'),
      undo: this.querySelector('[data-mixbox-undo]'),
      summary: this.querySelector('[data-mixbox-summary]'),
      price: this.querySelector('[data-mixbox-price]'),
      per: this.querySelector('[data-mixbox-per]'),
      add: this.querySelector('[data-mixbox-add]'),
      addLabel: this.querySelector('[data-mixbox-add-label]'),
      error: this.querySelector('[data-mixbox-error]'),
      bar: this.querySelector('[data-mixbox-bar]'),
      barInfo: this.querySelector('[data-mixbox-bar-info]'),
      barTotal: this.querySelector('[data-mixbox-bar-total]'),
      barSize: this.querySelector('[data-mixbox-bar-size]'),
      barProgress: this.querySelector('[data-mixbox-bar-progress]'),
      barAdd: this.querySelector('[data-mixbox-bar-add]'),
      barLabel: this.querySelector('[data-mixbox-bar-label]'),
      announcer: this.querySelector('[data-mixbox-announcer]'),
    };

    this.restore();
    this.ensureAvailableSize();
    this.bindEvents();
    this.render({ animate: false });
    this.observeBar();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    document.documentElement.classList.remove('mixbox-bar-open');
  }

  /* ===== State helpers ===== */

  get currentSize() {
    const checked = this.els.sizeInputs.find((input) => input.checked);
    const variantId = checked ? Number(checked.value) : null;
    return this.sizes.find((size) => size.variantId === variantId) || this.sizes[0];
  }

  get availableFlavours() {
    return this.flavours.filter((flavour) => flavour.available);
  }

  countOf(flavourId) {
    return this.sequence.reduce((total, id) => (id === flavourId ? total + 1 : total), 0);
  }

  /* ===== Persistence (per-browser convenience only) ===== */

  restore() {
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
    } catch (error) {
      saved = null;
    }
    if (!saved || !Array.isArray(saved.sequence)) return;

    this.sequence = saved.sequence
      .map(String)
      .filter((id) => this.flavourById.has(id) && this.flavourById.get(id).available);

    /* An explicit ?variant= in the URL wins over the remembered size */
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (!urlVariant && saved.variantId) {
      const input = this.els.sizeInputs.find((el) => Number(el.value) === Number(saved.variantId) && !el.disabled);
      if (input) input.checked = true;
    }
  }

  /* ?variant= (or a stale remembered size) can point at a sold-out / non-pack variant:
     fall back to the first size that can actually be bought. */
  ensureAvailableSize() {
    const checked = this.els.sizeInputs.find((input) => input.checked);
    if (checked && !checked.disabled) return;
    const fallback = this.els.sizeInputs.find((input) => !input.disabled);
    if (!fallback) return;
    fallback.checked = true;
    this.syncUrl(fallback.value);
  }

  persist() {
    try {
      if (this.sequence.length === 0) {
        window.localStorage.removeItem(this.storageKey);
      } else {
        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({ variantId: this.currentSize.variantId, sequence: this.sequence })
        );
      }
    } catch (error) {
      /* Storage can be unavailable (private mode) — the builder works without it. */
    }
  }

  forget() {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      /* Non-fatal */
    }
  }

  /* ===== Events ===== */

  bindEvents() {
    this.els.sizeInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleSizeChange());
    });

    this.els.cards.forEach((card, flavourId) => {
      const inc = card.querySelector('[data-mixbox-inc]');
      const dec = card.querySelector('[data-mixbox-dec]');
      if (inc) inc.addEventListener('click', () => this.addFlavour(flavourId));
      if (dec) {
        dec.addEventListener('click', () => {
          if (dec.getAttribute('aria-disabled') !== 'true') this.removeFlavour(flavourId);
        });
      }
    });

    if (this.els.fill) {
      this.els.fill.addEventListener('click', () => {
        if (this.els.fill.getAttribute('aria-disabled') === 'true') this.nudge();
        else this.fillRest();
      });
    }
    if (this.els.clear) this.els.clear.addEventListener('click', () => this.clearAll());

    if (this.els.slots) {
      this.els.slots.addEventListener('click', (event) => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        const slot = event.target.closest('[data-slot-index]');
        if (!slot || !slot.classList.contains('is-filled')) return;
        this.removeAt(Number(slot.getAttribute('data-slot-index')));
      });
    }

    if (this.els.upsell) {
      this.els.upsell.addEventListener('click', () => {
        const target = this.els.upsell.getAttribute('data-variant-id');
        const input = this.els.sizeInputs.find((el) => el.value === target);
        if (!input) return;
        input.checked = true;
        this.handleSizeChange();
        input.focus({ preventScroll: true });
      });
    }

    if (this.els.undo) this.els.undo.addEventListener('click', () => this.undoClear());

    if (this.els.add) this.els.add.addEventListener('click', () => this.handleAdd());
    if (this.els.barAdd) this.els.barAdd.addEventListener('click', () => this.handleAdd());
    if (this.els.barInfo) {
      this.els.barInfo.addEventListener('click', () => {
        this.els.box.scrollIntoView({ behavior: this.scrollBehavior, block: 'center' });
      });
    }
  }

  get scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  handleSizeChange() {
    this.dropUndo();
    this.clearError();
    const size = this.currentSize;
    this.syncUrl(size.variantId);
    this.persist();
    this.render({ animate: false });

    const over = this.sequence.length - size.size;
    if (over > 0) {
      this.announce(`${size.size}-pack gekozen. Er passen er ${over} niet meer in.`);
    } else {
      this.announce(`${size.size}-pack gekozen. ${this.sequence.length} van ${size.size} gekozen.`);
    }
  }

  syncUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* Non-fatal */
    }
  }

  addFlavour(flavourId) {
    const size = this.currentSize;
    const flavour = this.flavourById.get(flavourId);
    if (!flavour || !flavour.available) return;

    if (this.sequence.length >= size.size) {
      this.nudge();
      this.announce(`Je pakket is vol. Haal er eerst een uit of kies een groter pakket.`);
      return;
    }

    this.dropUndo();
    this.clearError();
    this.sequence.push(flavourId);
    this.persist();
    this.render({ animate: true, bumped: flavourId });

    this.announce(`${flavour.title}: ${this.countOf(flavourId)}. ${this.progressPhrase()}`);
  }

  progressPhrase() {
    const remaining = this.currentSize.size - this.sequence.length;
    if (remaining === 0) return 'Je pakket is compleet.';
    if (remaining < 0) return `Er passen er ${-remaining} niet meer in.`;
    return `Nog ${remaining} te gaan.`;
  }

  removeFlavour(flavourId) {
    const index = this.sequence.lastIndexOf(flavourId);
    if (index === -1) return;
    this.removeAt(index);
  }

  removeAt(index) {
    const flavourId = this.sequence[index];
    if (flavourId === undefined) return;
    this.dropUndo();
    this.clearError();
    this.sequence.splice(index, 1);
    this.persist();
    this.render({ animate: false, bumped: flavourId });

    const flavour = this.flavourById.get(flavourId);
    this.announce(`${flavour ? flavour.title : 'Cocktail'}: ${this.countOf(flavourId)}. ${this.progressPhrase()}`);
  }

  /* Over-full after switching to a smaller pack: drop the most recently added ones. */
  trimOverflow() {
    const over = this.sequence.length - this.currentSize.size;
    if (over <= 0) return;
    this.sequence.splice(this.currentSize.size);
    this.persist();
    this.render({ animate: false });
    this.announce(`De laatste ${over} zijn eruit gehaald. Je pakket is compleet.`);
  }

  /* Fill the remaining slots evenly, topping up the least-picked flavours first. */
  fillRest() {
    const size = this.currentSize;
    const flavours = this.availableFlavours;
    if (!flavours.length || this.sequence.length >= size.size) return;

    this.dropUndo();
    const counts = new Map(flavours.map((flavour) => [flavour.id, this.countOf(flavour.id)]));
    const startLength = this.sequence.length;

    while (this.sequence.length < size.size) {
      let pick = flavours[0].id;
      flavours.forEach((flavour) => {
        if (counts.get(flavour.id) < counts.get(pick)) pick = flavour.id;
      });
      counts.set(pick, counts.get(pick) + 1);
      this.sequence.push(pick);
    }

    this.persist();
    this.render({ animate: true, fromIndex: startLength });
    const mix = flavours
      .map((flavour) => `${this.countOf(flavour.id)} ${flavour.title}`)
      .filter((part) => !part.startsWith('0 '))
      .join(', ');
    this.announce(`Je pakket is compleet: ${mix}.`);
  }

  clearAll() {
    if (!this.sequence.length) return;
    this.undoSnapshot = this.sequence.slice();
    this.sequence = [];
    this.persist();
    this.render({ animate: false });
    if (this.els.undo) this.els.undo.hidden = false;
    /* "Leegmaken" hides itself — hand focus to the next tool instead of <body>. */
    if (this.els.fill) this.els.fill.focus({ preventScroll: true });
    this.announce('Je pakket is leeggemaakt. Kies Ongedaan maken om het terug te zetten.');
  }

  undoClear() {
    if (!this.undoSnapshot) return;
    this.sequence = this.undoSnapshot;
    this.undoSnapshot = null;
    if (this.els.undo) this.els.undo.hidden = true;
    this.persist();
    this.render({ animate: true, fromIndex: 0 });
    if (this.els.clear) this.els.clear.focus({ preventScroll: true });
    this.announce(`Je pakket is teruggezet. ${this.progressPhrase()}`);
  }

  dropUndo() {
    if (!this.undoSnapshot) return;
    this.undoSnapshot = null;
    if (this.els.undo) this.els.undo.hidden = true;
  }

  /* ===== Rendering ===== */

  render({ animate = false, bumped = null, fromIndex = null } = {}) {
    const size = this.currentSize;
    if (!size) return;

    const total = this.sequence.length;
    const remaining = size.size - total;
    const isComplete = remaining === 0;
    const isOver = remaining < 0;

    this.classList.toggle('is-complete', isComplete);
    this.classList.toggle('is-over', isOver);

    /* Step copy */
    if (this.els.sizeText) this.els.sizeText.textContent = size.size;

    /* Flavour cards */
    this.els.cards.forEach((card, flavourId) => {
      const count = this.countOf(flavourId);
      const flavour = this.flavourById.get(flavourId);
      const countElement = card.querySelector('[data-mixbox-count]');
      const badge = card.querySelector('[data-mixbox-badge]');
      const inc = card.querySelector('[data-mixbox-inc]');
      const dec = card.querySelector('[data-mixbox-dec]');

      if (countElement) countElement.textContent = count;
      if (badge) {
        badge.hidden = count === 0;
        badge.textContent = `${count}×`;
        if (bumped === flavourId && count > 0 && animate) this.restartAnimation(badge, 'is-bump');
      }
      /* aria-disabled (not disabled) so a focused stepper keeps keyboard focus when
         it becomes unavailable, e.g. the moment the pack is full. */
      if (dec) {
        dec.disabled = false;
        dec.setAttribute('aria-disabled', count === 0 ? 'true' : 'false');
      }
      if (inc) {
        const full = total >= size.size;
        inc.setAttribute('aria-disabled', !flavour || !flavour.available || full ? 'true' : 'false');
        inc.setAttribute(
          'aria-label',
          full ? `Je pakket is vol, ${flavour ? flavour.title : ''} kan er niet meer bij` : `Eén ${flavour ? flavour.title : ''} toevoegen`
        );
      }
      card.classList.toggle('is-selected', count > 0);
    });

    /* Tools */
    if (this.els.fill) {
      this.els.fill.setAttribute('aria-disabled', total >= size.size || this.availableFlavours.length === 0 ? 'true' : 'false');
      if (this.els.fillLabel) {
        const label = total === 0 ? this.els.fill.getAttribute('data-label-empty') : this.els.fill.getAttribute('data-label-partial');
        if (label) this.els.fillLabel.textContent = label;
      }
    }
    if (this.els.clear) this.els.clear.hidden = total === 0;

    /* Box header + progress */
    if (this.els.boxTitle) this.els.boxTitle.textContent = `${size.size}-pack`;
    if (this.els.total) this.els.total.textContent = total;
    if (this.els.size) this.els.size.textContent = size.size;
    const ratio = Math.min(total / size.size, 1);
    if (this.els.progress) this.els.progress.style.transform = `scaleX(${ratio})`;

    this.renderSlots(size, { animate, fromIndex });

    if (this.els.overflow) {
      this.els.overflow.hidden = !isOver;
      if (isOver) this.els.overflow.textContent = `Er ${-remaining === 1 ? 'past er 1' : `passen er ${-remaining}`} niet meer in`;
    }

    /* Status + upsell */
    let status;
    if (total === 0) {
      status = `Nog leeg. Er passen ${size.size} cocktails in.`;
    } else if (isOver) {
      status = `Kies een groter pakket, of haal de laatste ${-remaining} eruit.`;
    } else if (isComplete) {
      status = 'Compleet! Je pakket is klaar om te bestellen.';
    } else {
      status = `Nog ${remaining} ${remaining === 1 ? 'cocktail' : 'cocktails'} te gaan.`;
    }
    if (this.els.status) this.els.status.textContent = status;

    this.renderUpsell(size, { isComplete, isOver, total });
    this.renderSummary();

    /* Price */
    if (this.els.price) this.els.price.textContent = size.price;
    if (this.els.per) this.els.per.textContent = `${size.perUnit} per cocktail`;

    /* Add buttons. Over-full turns the CTA into a (secondary) fix-it action. */
    let label;
    if (isComplete) {
      label = 'In winkelwagen';
    } else if (isOver) {
      label = `Haal de laatste ${-remaining} eruit`;
    } else if (total === 0) {
      label = 'Kies je cocktails';
    } else {
      label = `Nog ${remaining} kiezen`;
    }
    [this.els.add, this.els.barAdd].forEach((button) => {
      if (!button || button.classList.contains('is-added')) return;
      button.setAttribute('aria-disabled', isComplete || isOver ? 'false' : 'true');
      button.classList.toggle('is-secondary', isOver);
    });
    if (this.els.addLabel && !this.els.add.classList.contains('is-added')) {
      this.setLabel(this.els.addLabel, label, isComplete ? size.price : null);
    }
    if (this.els.barLabel && !this.els.barAdd.classList.contains('is-added')) {
      this.setLabel(this.els.barLabel, isOver ? `Haal er ${-remaining} uit` : label, null);
    }

    /* Sticky bar */
    if (this.els.barTotal) this.els.barTotal.textContent = total;
    if (this.els.barSize) this.els.barSize.textContent = size.size;
    if (this.els.barProgress) this.els.barProgress.style.transform = `scaleX(${ratio})`;
  }

  renderSlots(size, { animate, fromIndex }) {
    const container = this.els.slots;
    if (!container) return;

    if (container.getAttribute('data-size') !== String(size.size) || container.children.length !== size.size) {
      container.setAttribute('data-size', size.size);
      container.innerHTML = '';
      for (let index = 0; index < size.size; index += 1) {
        const slot = document.createElement('button');
        slot.type = 'button';
        slot.tabIndex = -1;
        slot.className = 'mixbox__slot';
        slot.setAttribute('data-slot-index', index);
        container.appendChild(slot);
      }
    }

    Array.from(container.children).forEach((slot, index) => {
      const flavourId = this.sequence[index];
      const previous = slot.getAttribute('data-flavour') || '';
      const next = flavourId || '';
      if (previous === next) return;

      slot.setAttribute('data-flavour', next);
      slot.classList.remove('is-new');
      slot.innerHTML = '';

      if (!flavourId) {
        slot.classList.remove('is-filled', 'is-cover');
        slot.style.removeProperty('--slot-bg');
        slot.removeAttribute('title');
        return;
      }

      const flavour = this.flavourById.get(flavourId);
      slot.classList.add('is-filled');
      slot.classList.toggle('is-cover', !flavour.contain);
      slot.style.setProperty('--slot-bg', flavour.slotBg);
      slot.title = `${flavour.title} verwijderen`;
      if (flavour.slotImage) {
        const image = document.createElement('img');
        image.src = flavour.slotImage;
        image.alt = '';
        image.decoding = 'async';
        slot.appendChild(image);
      }

      const isFresh = animate && (fromIndex === null ? index === this.sequence.length - 1 : index >= fromIndex);
      if (isFresh && !previous) {
        if (fromIndex !== null) slot.style.animationDelay = `${(index - fromIndex) * 35}ms`;
        else slot.style.removeProperty('animation-delay');
        this.restartAnimation(slot, 'is-new');
      }
    });
  }

  renderUpsell(size, { isComplete, isOver, total }) {
    const upsell = this.els.upsell;
    if (!upsell) return;

    let target = null;
    if (isComplete || isOver) {
      target = this.sizes.find((candidate) => candidate.available && candidate.size > size.size && candidate.size >= total);
    }

    if (!target) {
      upsell.hidden = true;
      return;
    }

    upsell.hidden = false;
    upsell.setAttribute('data-variant-id', target.variantId);
    upsell.textContent = isOver
      ? `Maak er een ${target.size}-pack van (${target.price}) →`
      : `Meer zin? Maak er een ${target.size}-pack van →`;
  }

  renderSummary() {
    const summary = this.els.summary;
    if (!summary) return;

    const rows = this.flavours
      .map((flavour) => ({ flavour, count: this.countOf(flavour.id) }))
      .filter((row) => row.count > 0);

    summary.hidden = rows.length === 0;
    summary.innerHTML = '';
    rows.forEach(({ flavour, count }) => {
      const item = document.createElement('li');

      const swatch = document.createElement('span');
      swatch.className = 'mixbox__summary-dot';
      swatch.style.setProperty('--slot-bg', flavour.slotBg);
      if (flavour.slotImage && flavour.contain) {
        const image = document.createElement('img');
        image.src = flavour.slotImage;
        image.alt = '';
        swatch.appendChild(image);
      }

      const qty = document.createElement('span');
      qty.className = 'mixbox__summary-qty';
      qty.textContent = `${count}×`;

      const name = document.createElement('span');
      name.textContent = flavour.title;

      item.append(swatch, qty, name);
      summary.appendChild(item);
    });
  }

  /* "In winkelwagen · €71,40" — the separator is decorative, keep it out of the accessible name. */
  setLabel(element, text, price) {
    element.textContent = text;
    if (!price) return;
    const separator = document.createElement('span');
    separator.setAttribute('aria-hidden', 'true');
    separator.textContent = ' · ';
    const amount = document.createElement('span');
    amount.className = 'visually-hidden';
    amount.textContent = ', ';
    element.append(amount, separator, price);
  }

  restartAnimation(element, className) {
    element.classList.remove(className);
    void element.offsetWidth; // reflow so the animation restarts
    element.classList.add(className);
  }

  announce(message) {
    if (!this.els.announcer) return;
    this.els.announcer.textContent = '';
    window.setTimeout(() => {
      this.els.announcer.textContent = message;
    }, 50);
  }

  /* Pack is full / incomplete feedback */
  nudge() {
    if (!this.els.status) return;
    this.restartAnimation(this.els.status, 'is-nudge');
  }

  /* ===== Add to cart ===== */

  async handleAdd() {
    const size = this.currentSize;
    const total = this.sequence.length;

    if (total > size.size) {
      this.trimOverflow();
      return;
    }

    if (total !== size.size) {
      this.nudge();
      if (total < size.size && this.els.flavourStep) {
        const rect = this.els.flavourStep.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (!inView || window.innerWidth < 1024) {
          this.els.flavourStep.scrollIntoView({ behavior: this.scrollBehavior, block: 'start' });
        }
      } else if (this.els.box && window.innerWidth < 1024) {
        this.els.box.scrollIntoView({ behavior: this.scrollBehavior, block: 'center' });
      }
      this.announce(this.els.status ? this.els.status.textContent : '');
      return;
    }

    if (this.isBusy || (this.els.add && this.els.add.classList.contains('is-added'))) return;
    this.isBusy = true;
    this.clearError();
    this.setLoading(true);

    const submitted = this.sequence.join(',');
    const body = new FormData();
    body.append('id', size.variantId);
    body.append('quantity', '1');
    this.compositionProperties().forEach(([name, count]) => {
      body.append(`properties[${name}]`, String(count));
    });
    body.append('sections', 'cart-drawer');

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const json = await response.json();

      /* Anything but a clean 2xx cart payload is a failure (422 sold out, 404 variant gone, 429, 5xx …) */
      if (!response.ok || json.status) {
        this.showError(json.description || json.message);
        return;
      }

      /* The pack is in the cart — forget the remembered draft (unless it was edited
         mid-request), keep the UI as-is so an identical second pack is one click away. */
      if (this.sequence.join(',') === submitted) this.forget();

      if (window.eventBus) {
        window.eventBus.emit('update:cart:drawer', json);
        this.updateCartCount();
        this.showAdded();
        window.eventBus.emit('open:cart:drawer');
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      console.error('Motail mixbox: unable to add to cart', error);
      this.showError();
    } finally {
      this.setLoading(false);
      this.isBusy = false;
    }
  }

  /* One line item property per flavour, in list order: [["Pornstar Martini", 4], …].
     Titles are the keys, so merge duplicates and strip brackets (Rails-style
     properties[a[b]] parsing would nest them and drop the value). */
  compositionProperties() {
    const totals = new Map();
    this.flavours.forEach((flavour) => {
      const count = this.countOf(flavour.id);
      if (!count) return;
      const name = String(flavour.title).replace(/\[/g, '(').replace(/\]/g, ')').trim() || 'Cocktail';
      totals.set(name, (totals.get(name) || 0) + count);
    });
    return Array.from(totals.entries());
  }

  async updateCartCount() {
    if (!window.wetheme || typeof window.wetheme.updateCartCount !== 'function') return;
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      const cart = await response.json();
      window.wetheme.updateCartCount(cart);
    } catch (error) {
      /* Count refresh is non-fatal — the drawer already shows the new item. */
    }
  }

  setLoading(loading) {
    [this.els.add, this.els.barAdd].forEach((button) => {
      if (!button) return;
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
    });
  }

  showAdded() {
    [
      [this.els.add, this.els.addLabel, 'Toegevoegd aan winkelwagen'],
      [this.els.barAdd, this.els.barLabel, 'Toegevoegd'],
    ].forEach(([button, label, text]) => {
      if (!button || !label) return;
      button.classList.add('is-added');
      label.textContent = text;
    });
    this.announce('Je pakket is toegevoegd aan de winkelwagen.');

    window.clearTimeout(this.addedTimer);
    this.addedTimer = window.setTimeout(() => {
      [this.els.add, this.els.barAdd].forEach((button) => button && button.classList.remove('is-added'));
      this.render({ animate: false });
    }, 2200);
  }

  showError(message) {
    if (!this.els.error) return;
    this.els.error.textContent = message || 'Er ging iets mis bij het toevoegen. Probeer het opnieuw.';
  }

  clearError() {
    if (!this.els.error || !this.els.error.textContent) return;
    this.els.error.textContent = '';
  }

  /* ===== Sticky bar (below 1024px) =====
     Visible while the builder is on screen but the box's own add button is not. */
  observeBar() {
    const bar = this.els.bar;
    if (!bar || !('IntersectionObserver' in window)) return;

    this.visibility = { section: false, add: false };
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.els.add) this.visibility.add = entry.isIntersecting;
          else this.visibility.section = entry.isIntersecting;
        });
        this.updateBar();
      },
      { threshold: 0 }
    );
    const layout = this.querySelector('.mixbox__layout');
    if (layout) this.observer.observe(layout);
    if (this.els.add) this.observer.observe(this.els.add);

    this.handleResize = () => this.updateBar();
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  updateBar() {
    const bar = this.els.bar;
    if (!bar || !this.visibility) return;
    const isSmall = window.innerWidth < 1024;
    const show = isSmall && this.visibility.section && !this.visibility.add;
    bar.classList.toggle('is-visible', show);
    document.documentElement.classList.toggle('mixbox-bar-open', show);
    if (this.sectionElement) this.sectionElement.classList.toggle('has-bar', isSmall);
  }
}

if (!customElements.get('motail-mixbox')) {
  customElements.define('motail-mixbox', MotailMixbox);
}
